import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import ContainerBox from './ContainerBox';
import './DataAnalysisContainer.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DataAnalysisContainer = ({ analysis, visible, onAnalysisComplete, onTargetSelectionReady, droppedColumns = [] }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // 'idle', 'starting', 'polling', 'complete'
  const [showAllCorrelations, setShowAllCorrelations] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [currentDroppedColumns, setCurrentDroppedColumns] = useState(new Set(droppedColumns));
  const [isStale, setIsStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const isReady = analysisData && !loading;

  // Update dropped columns when prop changes
  useEffect(() => {
    if (droppedColumns && droppedColumns.length >= 0) {
      setCurrentDroppedColumns(new Set(droppedColumns));
    }
  }, [droppedColumns]);

  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id) {
      // Simple API call - backend handles all the logic
      checkAnalysisStatus();
      checkStaleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid re-running on fn identity changes
  }, [visible, analysis?.datasets]);

  // Check stale status when dropped columns change
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && droppedColumns) {
      checkStaleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid re-running on fn identity changes
  }, [droppedColumns]);

  // Fade out scroll indicator after 30 seconds or 100px scroll when showing all correlations
  useEffect(() => {
    if (showAllCorrelations) {
      setShowScrollIndicator(true); // Reset when expanding

      // Timer for 30 seconds auto-fade
      const timer = setTimeout(() => {
        setShowScrollIndicator(false);
      }, 30000); // 30 seconds

      // Scroll listener for 100px scroll
      const correlationsGrid = document.querySelector('.correlations-grid--expanded');

      const handleScroll = (e) => {
        if (e.target.scrollTop >= 100) {
          setShowScrollIndicator(false);
        }
      };

      if (correlationsGrid) {
        correlationsGrid.addEventListener('scroll', handleScroll);
      }

      return () => {
        clearTimeout(timer);
        if (correlationsGrid) {
          correlationsGrid.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [showAllCorrelations]);

  const startPolling = () => {
    if (!analysis?.datasets?.[0]?.id) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const { apiService } = await import('../services/api');
        const response = await apiService.getAnalysisStatus(analysis.datasets[0].id);
        const status = response.data;
        
        console.log('Polling status:', status.status);
        
        // Backend handles all the logic
        if (status.status === 'complete' && status.analysis_data) {
          // Analysis complete with data
          clearInterval(pollInterval);
          setAnalysisData(status.analysis_data);
          setAnalysisStatus('complete');
          setLoading(false);
          onAnalysisComplete?.(status.analysis_data);
          console.log('Analysis complete!');
          
          // Don't auto-show target selection - wait for user to click button
        } else if (status.status === 'partial' && status.analysis_data) {
          // Partial results - features available but correlations still loading
          setAnalysisData(status.analysis_data);
          setAnalysisStatus('partial');
          setLoading(false);
          console.log('Partial analysis data received');
        } else if (status.status === 'ready') {
          // Ready to start - stop polling
          clearInterval(pollInterval);
          setAnalysisStatus('ready');
          setLoading(false);
          console.log('Analysis ready to start');
        } else if (status.status === 'error') {
          // Analysis failed
          clearInterval(pollInterval);
          setError(status.message || 'Analysis failed');
          setAnalysisStatus('idle');
          setLoading(false);
        }
        // Continue polling for 'starting' or other in-progress statuses
        
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(pollInterval);
        setError('Failed to get analysis results. Please try again.');
        setAnalysisStatus('idle');
        setLoading(false);
      }
    }, 2000); // Poll every 2 seconds
    
    // Clear interval after 2 minutes to prevent infinite polling
    setTimeout(() => {
      clearInterval(pollInterval);
      if (analysisStatus === 'polling') {
        setError('Analysis timed out. Please try again.');
        setAnalysisStatus('idle');
        setLoading(false);
      }
    }, 120000); // 2 minutes
  };

  const checkStaleStatus = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getArtifactStatus(analysis.datasets[0].id, 'analysis');
      const status = response.data;

      setIsStale(status.is_stale);
    } catch (err) {
      console.error('Failed to check stale status:', err);
    }
  };

  const handleRefresh = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    setRefreshing(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      await apiService.refreshAnalysisArtifacts(analysis.datasets[0].id);

      // After refresh, check status again to get new data
      await checkAnalysisStatus();
      setIsStale(false);
    } catch (err) {
      console.error('Failed to refresh analysis:', err);
      setError('Failed to refresh analysis. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const checkAnalysisStatus = async () => {
    if (!analysis?.datasets?.[0]?.id) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getAnalysisStatus(analysis.datasets[0].id);
      const status = response.data;

      // Backend handles all the logic and returns processed data
      if (status.status === 'complete' && status.analysis_data) {
        // Analysis complete with data
        setAnalysisData(status.analysis_data);
        setAnalysisStatus('complete');
        onAnalysisComplete?.(status.analysis_data);

        // Don't auto-show target selection - wait for user to click button
      } else if (status.status === 'partial' && status.analysis_data) {
        // Partial results - features available but correlations still loading
        setAnalysisData(status.analysis_data);
        setAnalysisStatus('partial');
        setLoading(true);
        startPolling();
      } else if (status.status === 'starting') {
        // Analysis started, begin polling
        setAnalysisStatus('polling');
        setLoading(true);
        startPolling();
      } else if (status.status === 'ready') {
        // Ready to start - auto-start analysis
        setLoading(true);
        setError(null);
        try {
          const { apiService } = await import('../services/api');
          await apiService.analyzeDataset(analysis.datasets[0].id);
          setAnalysisStatus('starting');
          startPolling();
        } catch (err) {
          console.error('Failed to start analysis:', err);
          setError('Failed to start analysis. Please try again.');
          setLoading(false);
          setAnalysisStatus('ready');
        }
      } else if (status.status === 'idle') {
        // Ready to start
        setAnalysisStatus('idle');
      } else {
        // Error or other status
        setError(status.message || 'Unknown status');
        setAnalysisStatus('idle');
      }
    } catch (err) {
      console.error('Failed to check analysis status:', err);
      setError('Failed to check analysis status');
      setAnalysisStatus('idle');
    }
  };

  const createHistogramData = (feature) => {
    if (feature.type === 'numerical') {
      // Check if histogram data exists and has required fields
      if (!feature.histogram || !feature.histogram.bin_edges || !feature.histogram.counts) {
        return null;
      }

      const { counts, bin_edges } = feature.histogram;
      const labels = bin_edges.slice(0, -1).map((edge, i) =>
        `${edge.toFixed(2)}-${bin_edges[i + 1].toFixed(2)}`
      );

      return {
        labels,
        datasets: [{
          data: counts,
          backgroundColor: 'rgba(59, 130, 246, 0.6)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
        }]
      };
    } else if (feature.type === 'categorical') {
      // Check if histogram data exists and has required fields
      if (!feature.histogram || !feature.histogram.categories || !feature.histogram.counts) {
        return null;
      }

      const { categories, counts } = feature.histogram;
      return {
        labels: categories,
        datasets: [{
          data: counts,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: 'rgba(139, 92, 246, 1)',
          borderWidth: 1,
        }]
      };
    }
    // Return null for date type or any other unsupported types
    return null;
  };

  const createCorrelationGaugeData = (correlation) => {
    const absCorr = Math.abs(correlation.correlation);
    const remaining = 1 - absCorr;
    
    // Color based on correlation strength: red (low) to green (high)
    const red = Math.round(255 * (1 - absCorr));
    const green = Math.round(200 * absCorr); // Reduced from 255 to 200 for less bright green
    const color = `rgb(${red}, ${green}, 0)`;
    
    return {
      labels: ['Correlation', 'Remaining'],
      datasets: [{
        data: [absCorr, remaining],
        backgroundColor: [color, 'rgba(229, 231, 235, 0.3)'],
        borderWidth: 0,
        cutout: '70%',
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      x: { display: false },
      y: { display: false }
    }
  };

  const gaugeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    }
  };

  if (!visible) return null;

  return (
    <ContainerBox
      title="Overview on Data Quality"
      isReady={isReady}
      readyLabel="Target Selection"
      onReadyClick={() => {
        onTargetSelectionReady?.(null);
      }}
      isStale={isStale}
      onRefreshClick={handleRefresh}
      refreshing={refreshing}
    >
      {loading && (
        <div className="analysis-loading">
          <div className="loading-spinner"></div>
          <p>Analyzing your data...</p>
        </div>
      )}

      {error && (
        <div className="analysis-error">
          <p>{error}</p>
          <button className="btn btn-primary btn-sm" onClick={checkAnalysisStatus}>
            Retry Analysis
          </button>
        </div>
      )}

      {analysisData && (
        <div className="analysis-results">
          {/* Features Section */}
          <div className="analysis-section">
            <h3 className="analysis-section__title">
              Features
            </h3>
            <div className="features-scroll">
              {analysisData.features
                .filter(feature => !currentDroppedColumns.has(feature.name))
                .map((feature, index) => {
                const histogramData = createHistogramData(feature);
                return (
                  <div key={index} className="feature-card">
                    <div className="feature-card__header">
                      <span className={`feature-type feature-type--${feature.type}`}>
                        {feature.type}
                      </span>
                      <h4 className="feature-card__name">{feature.name}</h4>
                    </div>
                    
                    <div className="feature-card__chart">
                      {histogramData && (
                        <Bar data={histogramData} options={chartOptions} />
                      )}
                    </div>
                    
                    <div className="feature-card__stats">
                      {feature.type === 'numerical' && (
                        <>
                          <div className="stat-item">
                            <span className="stat-label">Mean:</span>
                            <span className="stat-value">{feature.stats.mean?.toFixed(2)}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Median:</span>
                            <span className="stat-value">{feature.stats.median?.toFixed(2)}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Min:</span>
                            <span className="stat-value">{feature.stats.min?.toFixed(2)}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Max:</span>
                            <span className="stat-value">{feature.stats.max?.toFixed(2)}</span>
                          </div>
                        </>
                      )}

                      {feature.type === 'categorical' && (
                        <>
                          <div className="stat-item">
                            <span className="stat-label">Most Common:</span>
                            <span className="stat-value">{feature.stats.most_common}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Unique Values:</span>
                            <span className="stat-value">{feature.stats.unique_count}</span>
                          </div>
                        </>
                      )}

                      {feature.type === 'date' && (
                        <>
                          {feature.stats.min_date && (
                            <div className="stat-item">
                              <span className="stat-label">Min Date:</span>
                              <span className="stat-value">{feature.stats.min_date}</span>
                            </div>
                          )}
                          {feature.stats.max_date && (
                            <div className="stat-item">
                              <span className="stat-label">Max Date:</span>
                              <span className="stat-value">{feature.stats.max_date}</span>
                            </div>
                          )}
                        </>
                      )}

                      <div className="stat-item">
                        <span className="stat-label">Count:</span>
                        <span className="stat-value">{feature.stats.count}</span>
                      </div>

                      {feature.stats.null_count > 0 && (
                        <div className="stat-item">
                          <span className="stat-label">Missing:</span>
                          <span className="stat-value">{feature.stats.null_count}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Warnings */}
                    {analysisData.warnings && analysisData.warnings
                      .filter(warning => warning.column === feature.name && !currentDroppedColumns.has(warning.column))
                      .map((warning, warnIndex) => (
                        <div key={warnIndex} className="feature-card__warning">
                          <svg 
                            className="feature-card__warning-icon" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                          </svg>
                          <span className="feature-card__warning-text">{warning.hint || warning.issue}</span>
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Correlations Section */}
          {analysisStatus === 'partial' && (!analysisData.correlations || analysisData.correlations.length === 0) && (
            <div className="analysis-section">
              <h3 className="analysis-section__title">Correlations</h3>
              <div className="correlations-loading">
                <div className="loading-spinner"></div>
                <p>Computing correlations...</p>
              </div>
            </div>
          )}
          {analysisData.correlations && analysisData.correlations.length > 0 && (
            <div className="analysis-section">
              <h3 className="analysis-section__title">Correlations</h3>
              <div className={`correlations-wrapper ${showAllCorrelations ? 'correlations-wrapper--expanded' : ''}`}>
                <div className={`correlations-grid ${showAllCorrelations ? 'correlations-grid--expanded' : ''}`}>
                  {(() => {
                    // Filter first to remove dropped columns
                    const filteredCorrelations = analysisData.correlations.filter(
                      corr => !currentDroppedColumns.has(corr.feature1) && !currentDroppedColumns.has(corr.feature2)
                    );
                    // Then take top 5 or all depending on showAllCorrelations
                    const displayedCorrelations = showAllCorrelations
                      ? filteredCorrelations
                      : filteredCorrelations.slice(0, 5);

                    return displayedCorrelations.map((corr, index) => {
                      const gaugeData = createCorrelationGaugeData(corr);
                      return (
                        <div key={index} className="correlation-card">
                          <div className="correlation-card__chart">
                            <Doughnut data={gaugeData} options={gaugeOptions} />
                            <div className="correlation-card__value">
                              {Math.abs(corr.correlation).toFixed(2)}
                            </div>
                          </div>
                          <div className="correlation-card__labels">
                            <div className="correlation-card__feature">{corr.feature1}</div>
                            <div className="correlation-card__vs">vs</div>
                            <div className="correlation-card__feature">{corr.feature2}</div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {showAllCorrelations && (
                  <div className={`correlations-scroll-indicator ${!showScrollIndicator ? 'fade-out' : ''}`}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M7 13l5 5 5-5M7 6l5 5 5-5"/>
                    </svg>
                    <span>Scroll to see more</span>
                  </div>
                )}
              </div>
              {(() => {
                const filteredCorrelations = analysisData.correlations.filter(
                  corr => !currentDroppedColumns.has(corr.feature1) && !currentDroppedColumns.has(corr.feature2)
                );
                return (
                  <>
                    {!showAllCorrelations && filteredCorrelations.length > 5 && (
                      <div className="correlations-view-more" onClick={() => setShowAllCorrelations(true)}>
                        View More
                      </div>
                    )}
                    {showAllCorrelations && filteredCorrelations.length > 5 && (
                      <div className="correlations-view-more" onClick={() => setShowAllCorrelations(false)}>
                        View Less
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Summary */}
          <div className="analysis-summary">
            <p>
              Analyzed {analysisData.total_rows} rows with {analysisData.remaining_columns} features
              {analysisData.dropped_columns && 
               (analysisData.dropped_columns.user_selected?.length || 0) + (analysisData.dropped_columns.auto_detected?.length || 0) > 0 && (
                <span> (dropped {(analysisData.dropped_columns.user_selected?.length || 0) + (analysisData.dropped_columns.auto_detected?.length || 0)} columns: {(analysisData.dropped_columns.auto_detected.concat(analysisData.dropped_columns.user_selected)).join(", ")})</span>
              )}
            </p>
          </div>
        </div>
      )}
    </ContainerBox>
  );
};

export default DataAnalysisContainer;